import { FormHelperTextProps, SxProps, Theme } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { deDE, enUS } from "@mui/x-date-pickers/locales";
import { useDateLocale } from "core/hooks/useDateLocale";
import { InputWidthType, MUIPickersDate } from "core/types";
import { fromUnixTime, getUnixTime, set } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { TextInputFieldProps } from "ds_legacy/components/TextInputField";
import { GREY_600, WHITE } from "ds_legacy/materials/colors";
import {
  Z_INDEX_DRAWER_CONTENT,
  inputWidth,
} from "ds_legacy/materials/metrics";
import { FONT_SIZE_14 } from "ds_legacy/materials/typography";
import { FormElement, FormElementProps, isValid } from "react-forms-state";
import { useTranslations } from "translations";
import { getHelperText } from "../Validation";

export type ConnectedDateSelectorProps = FormElementProps &
  Omit<DateSelectorProps, "onChange">;

export type DateSelectorProps = {
  ariaLabel?: string;
  ariaLabelledBy?: string;
  blockAfter?: number;
  blockBefore?: number;
  shouldDisableFuture?: boolean;
  shouldDisablePast?: boolean;
  isDisabled?: boolean;
  elementName?: string;
  formHelperTextProps?: Partial<FormHelperTextProps>;
  isFullWidth?: boolean;
  hasCustomValidation?: boolean;
  id?: string;
  inputSx?: SxProps<Theme>;
  inputWidthType?: InputWidthType;
  label?: string;
  onChange: (v: number | null) => void;
  placeholder?: string;
  isRequired?: boolean;
  showToolbar?: boolean;
  sx?: SxProps<Theme>;
  textInputVariant?: TextInputFieldProps["variant"];
  validation?: FormElementProps["validation"];
  value?: number | null;
};

export function getLocaleText(value: string | undefined) {
  let locale = enUS;
  if (value === "de") {
    locale = deDE;
  }

  return locale.components.MuiLocalizationProvider.defaultProps.localeText;
}

export const isValidDate = (dateValue: number | null) => {
  if (!dateValue) return false;
  const selectedDate = fromUnixTime(dateValue);

  return isValid(selectedDate) && selectedDate.getFullYear() > 1900;
};

export function DatePickerInputField({
  ariaLabel,
  ariaLabelledBy,
  blockAfter,
  blockBefore,
  elementName,
  formHelperTextProps,
  hasCustomValidation,
  id,
  inputSx,
  inputWidthType,
  isDisabled,
  isFullWidth = false,
  isRequired,
  label,
  onChange,
  placeholder,
  shouldDisableFuture,
  shouldDisablePast,
  showToolbar = false,
  sx,
  textInputVariant = "standard",
  validation,
  value,
}: DateSelectorProps) {
  const { currentLocale } = useDateLocale();
  const translations = useTranslations();
  const pickerInputWidth = inputWidth(inputWidthType);
  const maxDate = blockAfter ? fromUnixTime(blockAfter) : undefined;
  const minDate =
    blockBefore || shouldDisablePast
      ? blockBefore
        ? fromUnixTime(blockBefore)
        : new Date()
      : undefined;

  return (
    <LocalizationProvider
      adapterLocale={currentLocale}
      dateAdapter={AdapterDateFns}
      localeText={getLocaleText(currentLocale.code)}
    >
      <DesktopDatePicker
        disabled={isDisabled}
        disableFuture={shouldDisableFuture}
        disablePast={shouldDisablePast}
        maxDate={maxDate}
        minDate={minDate}
        slotProps={{
          openPickerButton: {
            ...({
              "aria-hidden": true, // for accessibility reasons
              "data-testid": `${elementName}_open`,
              tabIndex: -1, // for accessibility reasons
            } as any),
          },
          popper: { style: { zIndex: Z_INDEX_DRAWER_CONTENT } },
          textField: {
            error: !isValid(validation),
            FormHelperTextProps: formHelperTextProps,
            fullWidth: isFullWidth,
            helperText: getHelperText({
              hasCustomValidation: !!hasCustomValidation,
              translations,
              validation,
            }),
            id: id ?? elementName,
            InputLabelProps: {
              style: { fontSize: FONT_SIZE_14 },
            },
            InputProps: {
              name: elementName,
              sx: {
                "&.MuiOutlinedInput-root": {
                  backgroundColor: WHITE,
                },
              },
            },
            inputProps: {
              "aria-label": ariaLabel,
              "aria-labelledby": ariaLabelledBy,
              "data-testid": `input_text_${elementName}`,
              placeholder: translations.patient.dateOfBirthFormat,
              style: {
                fontSize: FONT_SIZE_14,
              },
            },
            label,
            placeholder,
            required: isRequired,
            size: "small",
            sx: {
              "input::placeholder": { color: GREY_600, opacity: 1 },
              width: pickerInputWidth,
              ...inputSx,
            },
            variant: textInputVariant,
          },
          toolbar: { hidden: !showToolbar },
        }}
        sx={sx}
        value={value && isValidDate(value) ? fromUnixTime(value) : null}
        onChange={(date: MUIPickersDate) => {
          if (!date) {
            return onChange(null);
          }
          // date picker is not intended to set time
          // normalize to 12 pm (midday)
          // this ensures timezones on start/end of days don't change the day
          // important e.g. for day filtering logic in the BE
          const normalizedDate = zonedTimeToUtc(
            set(date, { hours: 12 }),
            "UTC",
          );
          const normalizedUnixTime = getUnixTime(normalizedDate);
          if (!isValidDate(normalizedUnixTime)) {
            return onChange(null);
          }
          onChange(normalizedUnixTime);
          return normalizedUnixTime;
        }}
      />
    </LocalizationProvider>
  );
}

const ConnectedDateInputField =
  FormElement()<ConnectedDateSelectorProps>(DatePickerInputField);

export default ConnectedDateInputField;
